/**
 * Lightweight markdown renderer using react-markdown
 * Optimized for chat messages with minimal plugins
 */

import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'

// Custom components for markdown elements
const components: Components = {
  // Headers - proper spacing for readability
  h1: ({ children }) => <h1 className="mt-6 mb-1 text-2xl leading-tight font-bold">{children}</h1>,
  h2: ({ children }) => <h2 className="mt-5 mb-1 text-xl leading-tight font-bold">{children}</h2>,
  h3: ({ children }) => (
    <h3 className="mt-4 mb-1 text-lg leading-snug font-semibold">{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className="mt-3 mb-1 text-base leading-snug font-semibold">{children}</h4>
  ),
  h5: ({ children }) => (
    <h5 className="mt-3 mb-1 text-sm leading-normal font-medium">{children}</h5>
  ),
  h6: ({ children }) => (
    <h6 className="text-muted-foreground mt-3 mb-1 text-xs leading-normal font-medium">
      {children}
    </h6>
  ),

  // Paragraphs with optimal line height for readability
  p: ({ children }) => <p className="leading-[1.6] last:mb-0">{children}</p>,

  // Lists with proper spacing
  ul: ({ children }) => <ul className="ml-6 list-disc space-y-1 leading-[1.6]">{children}</ul>,
  ol: ({ children }) => (
    <ol className="mt-1 ml-6 list-decimal space-y-1 leading-[1.6]">{children}</ol>
  ),
  li: ({ children }) => <li className="leading-[1.6]">{children}</li>,

  // Inline elements
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  code: ({ children }) => (
    <code className="bg-muted rounded px-1 py-0.5 font-mono text-sm">{children}</code>
  ),

  // Code blocks
  pre: ({ children }) => (
    <pre className="bg-muted mb-2 overflow-x-auto rounded-md p-4 font-mono text-sm leading-[1.5]">
      {children}
    </pre>
  ),

  // Links
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-500 underline hover:text-blue-600"
    >
      {children}
    </a>
  ),

  // Blockquotes
  blockquote: ({ children }) => (
    <blockquote className="border-muted text-muted-foreground my-4 border-l-4 pl-4 leading-[1.6] italic">
      {children}
    </blockquote>
  )
}

export const renderMarkdown = (content: string) => {
  return (
    <div className="text-sm break-words">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </div>
  )
}
